import { z } from 'zod'
import { DATE_INPUT_DESCRIPTION, DateInputSchema, DateRangeSchema } from '../time'
import { RoleIdSchema } from '../role'
import { ProgramUserStatusSchema } from '../program'
import { LatLngSchema } from '../locations'
import { CompanyIdSchema, TeamIdSchema, UserIdSchema, WorkflowIdSchema } from '../ids'
import { TeamLevelSchema } from '../entity'
import { CustomFieldFilterSchema } from '../customFieldMap'
import { CustomFieldIdSchema } from '../customField'
import { CapitalizeWeekdaySchema } from '../closer'

const REPORT_KPI_DATE_INPUT_DESCRIPTION =
  DATE_INPUT_DESCRIPTION + " Zone-less strings use America/Los_Angeles, not the querying user's timezone."

export const REPORT_KPI_DATE_FILTER_DESCRIPTION =
  'Date fields are inclusive instants when a KPI supports date filtering. They filter the date field for that KPI: ' +
  'for example, workflow actions use their action timestamp, account-created/assigned/status KPIs use the corresponding ' +
  'account date, and setter/closer stage counts use stage-entry time—not account last-action time. User access/count fields ' +
  'ignore this report date range, so the dates do not have one universal account-creation or last-action meaning across all ' +
  'KPI types. On the account-history path, which serves setter/closer stage counts and account custom-field and ratio KPIs, ' +
  '`startDate` and `endDate` are independently optional: start-only applies a lower bound, end-only applies an upper bound, ' +
  'both apply a closed range, and neither applies no date filter. ' +
  REPORT_KPI_DATE_INPUT_DESCRIPTION +
  ' Equal start/end values select only that instant, not a whole day. For one calendar day, send its start and inclusive end, ' +
  'for example `2026-01-01T00:00:00.000Z` through `2026-01-01T23:59:59.999Z`.'

export const ReportKpiFilterSchema = z
  .object({
    companyId: CompanyIdSchema.optional().meta({ description: "Defaults to the current user's company." }),
    startDate: DateInputSchema.optional().meta({
      description:
        'Optional inclusive lower bound when the KPI supports date filtering. It is enforced without `endDate`. ' +
        REPORT_KPI_DATE_INPUT_DESCRIPTION,
      example: '2026-01-01T00:00:00.000Z',
    }),
    endDate: DateInputSchema.optional().meta({
      description:
        'Optional inclusive upper bound when the KPI supports date filtering. It is enforced without `startDate`. Equal boundaries select one instant, not a whole day. ' +
        REPORT_KPI_DATE_INPUT_DESCRIPTION,
      example: '2026-01-01T23:59:59.999Z',
    }),
    teams: TeamIdSchema.array().optional().meta({ description: 'Scopes activity to these teams.' }),
    users: UserIdSchema.array().optional().meta({
      description: "Matches the KPI's actor, setter, closer, or task owner.",
    }),
    roles: RoleIdSchema.array().optional(),
    managers: UserIdSchema.array().optional(),
    workflowId: WorkflowIdSchema.optional(),
    polygon: z.array(LatLngSchema).optional().meta({
      description: 'Filters property locations when at least three points are provided.',
    }),
    days: CapitalizeWeekdaySchema.array().optional(),
    states: z.string().array().optional().meta({
      description: 'State codes, e.g. CA.',
    }),
    cities: z.string().array().optional(),
    zipCodes: z.string().array().optional(),
    customFields: CustomFieldFilterSchema.optional(),
    programUserStatus: ProgramUserStatusSchema.optional().meta({
      description: 'Applies only to task and program KPIs.',
    }),
    programUserStartDate: DateRangeSchema.optional().meta({
      description: 'Inclusive; applies only to task and program KPIs.',
    }),
  })
  .meta({
    description: 'Limits the report population and date range. ' + REPORT_KPI_DATE_FILTER_DESCRIPTION,
    example: {
      startDate: '2026-01-01T00:00:00.000Z',
      endDate: '2026-01-01T23:59:59.999Z',
    },
  })
export type ReportKpiFilter = z.infer<typeof ReportKpiFilterSchema>

const ReportKpiGroupingCompanySchema = z.object({
  type: z.literal('company'),
})

export const ReportKpiGroupingUserSchema = z.object({
  type: z.literal('user'),
})

export const ReportKpiGroupingTeamSchema = z.object({
  type: z.literal('team'),
  level: TeamLevelSchema.meta({ description: 'Team hierarchy level used to group rows.' }),
  include: z.array(z.string()).optional().meta({
    description: 'Derived from filter.teams; clients normally omit it.',
  }),
})
export type ReportKpiGroupingTeam = z.infer<typeof ReportKpiGroupingTeamSchema>

export const REPORT_TIME_PERIODS = ['hour', 'day', 'week', 'month', 'quarter', 'year'] as const
export const ReportTimePeriodSchema = z.enum(REPORT_TIME_PERIODS)
export type ReportTimePeriod = z.infer<typeof ReportTimePeriodSchema>
export const isReportTimePeriod = (value: string): value is ReportTimePeriod =>
  ReportTimePeriodSchema.safeParse(value).success

export const ReportKpiGroupingTimePeriodSchema = z.object({
  type: z.literal('time'),
  period: ReportTimePeriodSchema.meta({
    description: "Calendar periods use the querying user's timezone.",
  }),
})
export type ReportKpiGroupingTimePeriod = z.infer<typeof ReportKpiGroupingTimePeriodSchema>

export const ReportKpiGroupingManagerSchema = z.object({
  type: z.literal('manager'),
  level: TeamLevelSchema.optional().meta({
    description: 'Uses manager groupings stored at this team level.',
  }),
})

const ReportKpiGroupingStateSchema = z.object({
  type: z.literal('state'),
})

const ReportKpiGroupingCitySchema = z.object({
  type: z.literal('city'),
})

const ReportKpiGroupingZipSchema = z.object({
  type: z.literal('zip'),
})

const ReportKpiGroupingCustomFieldSchema = z.object({
  type: z.literal('customField'),
  customFieldId: CustomFieldIdSchema,
})

const ReportKpiGroupingTaskSchema = z.object({
  type: z.literal('programTask'),
})
export type ReportKpiGroupingTask = z.infer<typeof ReportKpiGroupingTaskSchema>

export const ReportKpiGroupingSchema = z.discriminatedUnion('type', [
  ReportKpiGroupingUserSchema,
  ReportKpiGroupingCompanySchema,
  ReportKpiGroupingTeamSchema,
  ReportKpiGroupingTimePeriodSchema,
  ReportKpiGroupingManagerSchema,
  ReportKpiGroupingStateSchema,
  ReportKpiGroupingCitySchema,
  ReportKpiGroupingZipSchema,
  ReportKpiGroupingCustomFieldSchema,
  ReportKpiGroupingTaskSchema,
])
export type ReportKpiGrouping = z.infer<typeof ReportKpiGroupingSchema>

export const ReportTimeGroupingSchema = z.discriminatedUnion('type', [
  ReportKpiGroupingUserSchema,
  ReportKpiGroupingCompanySchema,
  ReportKpiGroupingTeamSchema,
  ReportKpiGroupingManagerSchema,
  ReportKpiGroupingStateSchema,
  ReportKpiGroupingCitySchema,
  ReportKpiGroupingZipSchema,
])
export type ReportTimeGrouping = z.infer<typeof ReportTimeGroupingSchema>
